import { Link } from 'react-router-dom';

import type { Workshop } from '../../../../api/workshops';
import type { PrivateEventReturn } from '../../../private-events/private-event-return';
import { FillBar, PrivateEventBadge } from '../../primitives';

import { crewName, EM_DASH } from './upcomingWorkshopsFormat';
import { WorkshopCrewCell } from './WorkshopCrewCell';
import { WorkshopDateCell } from './WorkshopDateCell';
import { WorkshopStatusCell } from './WorkshopStatusCell';

const VISUALLY_HIDDEN = {
  position: 'absolute',
  width: 1,
  height: 1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
} as const;

export function WorkshopsTableRow({
  w,
  detailHref,
  returnState,
  now,
}: {
  w: Workshop;
  detailHref: string;
  /** Lets a private event's page link back here. */
  returnState: PrivateEventReturn;
  now: Date;
}) {
  // Fill bar is driven by the ops-CONFIRMED `opFill` (coherent with paceStatus)
  // rather than the legacy warehouse `attendeesBooked`/`fillPercent`, so the
  // Fill column and the Status pill always agree. Falls back to capacity when
  // opFill is absent (graceful degradation / pre-enrichment).
  const booked = w.opFill?.bookedSeats ?? 0;
  const capacity = w.opFill?.capacity ?? w.capacity;
  return (
    <tr>
      <td>
        <Link
          to={detailHref}
          state={w.isPrivate ? returnState : undefined}
          style={{ color: 'inherit', textDecoration: 'none', display: 'block' }}
        >
          {w.isPrivate && <span style={VISUALLY_HIDDEN}>Private event, </span>}
          <WorkshopDateCell localStart={w.localStartDatetime} />
        </Link>
      </td>
      <td>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          {w.workshopType.treeSpecies?.name ?? EM_DASH}
          {w.isPrivate && <PrivateEventBadge />}
        </span>
      </td>
      <td>
        <WorkshopCrewCell name={crewName(w.crew?.teacher)} />
      </td>
      <td>
        <WorkshopCrewCell name={crewName(w.crew?.assistant)} />
      </td>
      <td>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <FillBar sold={booked} cap={capacity} ariaLabel={`${booked} of ${capacity} booked`} />
          <span className="nb-tabular nb-mono" style={{ fontSize: 11.5 }}>
            {booked}/{capacity}
          </span>
        </div>
      </td>
      <td>
        <WorkshopStatusCell w={w} now={now} />
      </td>
    </tr>
  );
}
